import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Palette,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Globe,
  Loader2,
  RefreshCw,
  Building2,
  Image as ImageIcon,
  Type,
  Settings,
  Eye,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  LayoutGrid,
  Paintbrush
} from 'lucide-react';
import { toast } from 'sonner';

// Default branding template
const DEFAULT_BRANDING = {
  company: {
    name: "",
    tagline: "",
    logo: "",
    logoLight: "",
    favicon: "",
    website: "",
    supportEmail: ""
  },
  theme: {
    colors: {
      primary: "#3B82F6",
      primaryHover: "#2563EB",
      secondary: "#10B981",
      accent: "#8B5CF6",
      background: "#0F172A",
      backgroundSecondary: "#1E293B",
      surface: "#1E293B",
      surfaceHover: "#334155",
      border: "#334155",
      text: "#F8FAFC",
      textSecondary: "#94A3B8",
      success: "#10B981",
      warning: "#F59E0B",
      error: "#EF4444",
      info: "#3B82F6"
    },
    fonts: {
      heading: "'Inter', sans-serif",
      body: "'Inter', sans-serif"
    },
    borderRadius: {
      sm: "0.375rem",
      md: "0.5rem",
      lg: "0.75rem",
      xl: "1rem"
    }
  },
  products: {
    finance: { name: "MyFinance Orbit", description: "Financial Management", icon: "DollarSign", color: "#10B981", enabled: true },
    hr: { name: "MyHR Orbit", description: "HR Management", icon: "Users", color: "#3B82F6", enabled: true },
    sales: { name: "MySales Orbit", description: "Sales CRM", icon: "TrendingUp", color: "#F59E0B", enabled: true },
    docs: { name: "MyDoc Orbit", description: "Document Intelligence", icon: "FileText", color: "#8B5CF6", enabled: true }
  },
  features: {
    showPoweredBy: true,
    enableDarkMode: true,
    enableChatbot: true,
    enableAnalytics: false,
    analyticsId: ""
  },
  footer: {
    copyright: "",
    links: []
  }
};

// Color input component
function ColorInput({ label, value, onChange, id }) {
  return (
    <div className="flex items-center gap-3">
      <div 
        className="w-8 h-8 rounded-md border border-slate-600 cursor-pointer overflow-hidden flex-shrink-0"
        style={{ backgroundColor: value }}
      >
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-full opacity-0 cursor-pointer"
          id={id}
        />
      </div>
      <div className="flex-1">
        <Label htmlFor={id} className="text-xs text-slate-400">{label}</Label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 text-xs bg-slate-800 border-slate-700"
          placeholder="#RRGGBB"
        />
      </div>
    </div>
  );
}

// Tenant Card Component
function TenantCard({ tenant, onEdit, onDelete, onView }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const copySubdomain = async () => {
    try {
      await navigator.clipboard.writeText(`${tenant.subdomain}.yourapp.com`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Clipboard API may fail in non-secure contexts
      console.log('Clipboard access denied');
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-700" data-testid={`tenant-card-${tenant.subdomain}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {tenant.company?.logo ? (
              <img 
                src={tenant.company.logo} 
                alt={tenant.company?.name}
                className="w-12 h-12 rounded-lg object-contain bg-white p-1"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-slate-500" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-white">{tenant.company?.name || 'Unnamed Tenant'}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs border-blue-500 text-blue-400">
                  {tenant.subdomain}
                </Badge>
                <button onClick={copySubdomain} className="text-slate-500 hover:text-slate-300">
                  {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-blue-400" onClick={() => onView(tenant)} data-testid={`preview-btn-${tenant.subdomain}`}>
              <Eye className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-white" onClick={() => onEdit(tenant)} data-testid={`edit-btn-${tenant.subdomain}`}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-400" onClick={() => onDelete(tenant.subdomain)} data-testid={`delete-btn-${tenant.subdomain}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Quick Info */}
        <div className="mt-4 pt-4 border-t border-slate-800">
          <button 
            onClick={() => setExpanded(!expanded)}
            className="flex items-center justify-between w-full text-sm text-slate-400 hover:text-white"
          >
            <span>Theme & Products</span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {expanded && (
            <div className="mt-3 space-y-3">
              {/* Color Preview */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Colors:</span>
                <div className="flex gap-1">
                  {['primary', 'secondary', 'accent', 'success', 'warning', 'error'].map(key => (
                    <div 
                      key={key}
                      className="w-5 h-5 rounded"
                      style={{ backgroundColor: tenant.theme?.colors?.[key] || '#666' }}
                      title={key}
                    />
                  ))}
                </div>
              </div>
              
              {/* Products */}
              <div className="flex flex-wrap gap-1">
                {tenant.products && Object.entries(tenant.products).map(([key, product]) => (
                  <Badge 
                    key={key} 
                    variant={product.enabled ? 'default' : 'secondary'}
                    className="text-xs"
                    style={product.enabled ? { backgroundColor: product.color + '20', color: product.color, borderColor: product.color } : {}}
                  >
                    {product.name}
                  </Badge>
                ))}
              </div>
              
              {/* Features */}
              <div className="flex gap-2 text-xs text-slate-500">
                {tenant.features?.showPoweredBy && <span>Powered By</span>}
                {tenant.features?.enableDarkMode && <span>Dark Mode</span>}
                {tenant.features?.enableChatbot && <span>Chatbot</span>}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function TenantBrandingPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    subdomain: '',
    ...DEFAULT_BRANDING
  });
  const [activeTab, setActiveTab] = useState('company');
  const [previewTenant, setPreviewTenant] = useState(null);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/branding/tenants`);
      setTenants(response.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleCreateTenant = async () => {
    if (!formData.subdomain) {
      toast.error('Subdomain is required');
      return;
    }
    if (!formData.company.name) {
      toast.error('Company name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        subdomain: formData.subdomain,
        company: formData.company,
        theme: formData.theme,
        products: formData.products,
        features: formData.features,
        footer: formData.footer
      };

      if (editingTenant) {
        await axios.patch(`${API}/branding/tenants/${editingTenant}`, payload);
        toast.success('Tenant updated successfully');
      } else {
        await axios.post(`${API}/branding/tenants`, payload);
        toast.success('Tenant created successfully');
      }
      
      fetchTenants();
      resetForm();
    } catch (error) {
      console.error('Error saving tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to save tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTenant = async (subdomain) => {
    if (!confirm(`Are you sure you want to delete tenant "${subdomain}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/branding/tenants/${subdomain}`);
      toast.success('Tenant deleted successfully');
      fetchTenants();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error('Failed to delete tenant');
    }
  };

  const startEditing = (tenant) => {
    setEditingTenant(tenant.subdomain);
    setFormData({
      subdomain: tenant.subdomain,
      company: { ...DEFAULT_BRANDING.company, ...tenant.company },
      theme: { 
        colors: { ...DEFAULT_BRANDING.theme.colors, ...tenant.theme?.colors },
        fonts: { ...DEFAULT_BRANDING.theme.fonts, ...tenant.theme?.fonts },
        borderRadius: { ...DEFAULT_BRANDING.theme.borderRadius, ...tenant.theme?.borderRadius }
      },
      products: { ...DEFAULT_BRANDING.products, ...tenant.products },
      features: { ...DEFAULT_BRANDING.features, ...tenant.features },
      footer: { ...DEFAULT_BRANDING.footer, ...tenant.footer }
    });
    setShowForm(true);
    setActiveTab('company');
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingTenant(null);
    setFormData({ subdomain: '', ...DEFAULT_BRANDING });
    setActiveTab('company');
  };

  const updateCompanyField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      company: { ...prev.company, [field]: value }
    }));
  };

  const updateThemeColor = (colorKey, value) => {
    setFormData(prev => ({
      ...prev,
      theme: {
        ...prev.theme,
        colors: { ...prev.theme.colors, [colorKey]: value }
      }
    }));
  };

  const updateProduct = (productKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      products: {
        ...prev.products,
        [productKey]: { ...prev.products[productKey], [field]: value }
      }
    }));
  };

  const updateFeature = (featureKey, value) => {
    setFormData(prev => ({
      ...prev,
      features: { ...prev.features, [featureKey]: value }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6" data-testid="tenant-branding-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Palette className="w-7 h-7 text-purple-500" />
            White-Label Tenant Management
          </h1>
          <p className="text-slate-400 mt-1">
            Configure custom branding for different client deployments
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTenants} className="border-slate-700 text-slate-300 hover:bg-slate-800">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Tenant
          </Button>
        </div>
      </div>

      {/* Preview Modal */}
      {previewTenant && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewTenant(null)}>
          <Card className="max-w-2xl w-full bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Preview: {previewTenant.company?.name}</CardTitle>
                <CardDescription className="text-slate-400">{previewTenant.subdomain}</CardDescription>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setPreviewTenant(null)}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Theme Preview */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: previewTenant.theme?.colors?.background || '#0F172A' }}>
                <div className="flex items-center gap-4 mb-4">
                  {previewTenant.company?.logo && (
                    <img src={previewTenant.company.logo} alt="" className="h-10 bg-white rounded p-1" />
                  )}
                  <div>
                    <h3 style={{ color: previewTenant.theme?.colors?.text || '#fff' }} className="font-bold">
                      {previewTenant.company?.name}
                    </h3>
                    <p style={{ color: previewTenant.theme?.colors?.textSecondary || '#94A3B8' }} className="text-sm">
                      {previewTenant.company?.tagline}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-4 py-2 rounded text-white text-sm"
                    style={{ backgroundColor: previewTenant.theme?.colors?.primary || '#3B82F6' }}
                  >
                    Primary Button
                  </button>
                  <button 
                    className="px-4 py-2 rounded text-white text-sm"
                    style={{ backgroundColor: previewTenant.theme?.colors?.secondary || '#10B981' }}
                  >
                    Secondary
                  </button>
                </div>
              </div>
              
              {/* Products */}
              <div>
                <h4 className="text-white font-medium mb-2">Enabled Products</h4>
                <div className="flex flex-wrap gap-2">
                  {previewTenant.products && Object.entries(previewTenant.products).filter(([_, p]) => p.enabled).map(([key, product]) => (
                    <Badge key={key} style={{ backgroundColor: product.color + '20', color: product.color, borderColor: product.color }} className="border">
                      {product.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="mb-8 bg-slate-900 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {editingTenant ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingTenant ? `Edit Tenant: ${editingTenant}` : 'Create New Tenant'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-slate-800 mb-6">
                <TabsTrigger value="company" className="data-[state=active]:bg-blue-600">
                  <Building2 className="w-4 h-4 mr-2" />
                  Company
                </TabsTrigger>
                <TabsTrigger value="theme" className="data-[state=active]:bg-blue-600">
                  <Paintbrush className="w-4 h-4 mr-2" />
                  Theme
                </TabsTrigger>
                <TabsTrigger value="products" className="data-[state=active]:bg-blue-600">
                  <LayoutGrid className="w-4 h-4 mr-2" />
                  Products
                </TabsTrigger>
                <TabsTrigger value="features" className="data-[state=active]:bg-blue-600">
                  <Settings className="w-4 h-4 mr-2" />
                  Features
                </TabsTrigger>
              </TabsList>

              {/* Company Tab */}
              <TabsContent value="company" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Subdomain *</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={formData.subdomain}
                        onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        placeholder="client-name"
                        disabled={!!editingTenant}
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="tenant-subdomain"
                      />
                      <span className="text-slate-500 text-sm">.yourapp.com</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Company Name *</Label>
                    <Input
                      value={formData.company.name}
                      onChange={(e) => updateCompanyField('name', e.target.value)}
                      placeholder="Acme Corporation"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="tenant-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Tagline</Label>
                    <Input
                      value={formData.company.tagline}
                      onChange={(e) => updateCompanyField('tagline', e.target.value)}
                      placeholder="Your AI Partner"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Support Email</Label>
                    <Input
                      type="email"
                      value={formData.company.supportEmail}
                      onChange={(e) => updateCompanyField('supportEmail', e.target.value)}
                      placeholder="support@acme.com"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Logo URL</Label>
                    <Input
                      value={formData.company.logo}
                      onChange={(e) => updateCompanyField('logo', e.target.value)}
                      placeholder="https://..."
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    {formData.company.logo && (
                      <img src={formData.company.logo} alt="Logo preview" className="h-10 mt-2 bg-white rounded p-1" onError={(e) => e.target.style.display = 'none'} />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Website URL</Label>
                    <Input
                      value={formData.company.website}
                      onChange={(e) => updateCompanyField('website', e.target.value)}
                      placeholder="https://acme.com"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Theme Tab */}
              <TabsContent value="theme" className="space-y-6">
                <div>
                  <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Brand Colors
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <ColorInput label="Primary" value={formData.theme.colors.primary} onChange={(v) => updateThemeColor('primary', v)} id="color-primary" />
                    <ColorInput label="Primary Hover" value={formData.theme.colors.primaryHover} onChange={(v) => updateThemeColor('primaryHover', v)} id="color-primaryHover" />
                    <ColorInput label="Secondary" value={formData.theme.colors.secondary} onChange={(v) => updateThemeColor('secondary', v)} id="color-secondary" />
                    <ColorInput label="Accent" value={formData.theme.colors.accent} onChange={(v) => updateThemeColor('accent', v)} id="color-accent" />
                  </div>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-4">Background Colors</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <ColorInput label="Background" value={formData.theme.colors.background} onChange={(v) => updateThemeColor('background', v)} id="color-bg" />
                    <ColorInput label="Surface" value={formData.theme.colors.surface} onChange={(v) => updateThemeColor('surface', v)} id="color-surface" />
                    <ColorInput label="Border" value={formData.theme.colors.border} onChange={(v) => updateThemeColor('border', v)} id="color-border" />
                    <ColorInput label="Surface Hover" value={formData.theme.colors.surfaceHover} onChange={(v) => updateThemeColor('surfaceHover', v)} id="color-surfaceHover" />
                  </div>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-4">Text & Status Colors</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <ColorInput label="Text" value={formData.theme.colors.text} onChange={(v) => updateThemeColor('text', v)} id="color-text" />
                    <ColorInput label="Text Secondary" value={formData.theme.colors.textSecondary} onChange={(v) => updateThemeColor('textSecondary', v)} id="color-textSec" />
                    <ColorInput label="Success" value={formData.theme.colors.success} onChange={(v) => updateThemeColor('success', v)} id="color-success" />
                    <ColorInput label="Warning" value={formData.theme.colors.warning} onChange={(v) => updateThemeColor('warning', v)} id="color-warning" />
                    <ColorInput label="Error" value={formData.theme.colors.error} onChange={(v) => updateThemeColor('error', v)} id="color-error" />
                    <ColorInput label="Info" value={formData.theme.colors.info} onChange={(v) => updateThemeColor('info', v)} id="color-info" />
                  </div>
                </div>
                
                {/* Color Preview */}
                <div className="p-4 rounded-lg border border-slate-700" style={{ backgroundColor: formData.theme.colors.background }}>
                  <p className="text-xs text-slate-500 mb-2">Live Preview</p>
                  <div className="flex flex-wrap gap-2">
                    <button className="px-4 py-2 rounded text-white text-sm" style={{ backgroundColor: formData.theme.colors.primary }}>Primary</button>
                    <button className="px-4 py-2 rounded text-white text-sm" style={{ backgroundColor: formData.theme.colors.secondary }}>Secondary</button>
                    <button className="px-4 py-2 rounded text-white text-sm" style={{ backgroundColor: formData.theme.colors.accent }}>Accent</button>
                    <button className="px-4 py-2 rounded text-white text-sm" style={{ backgroundColor: formData.theme.colors.success }}>Success</button>
                    <button className="px-4 py-2 rounded text-white text-sm" style={{ backgroundColor: formData.theme.colors.warning }}>Warning</button>
                    <button className="px-4 py-2 rounded text-white text-sm" style={{ backgroundColor: formData.theme.colors.error }}>Error</button>
                  </div>
                </div>
              </TabsContent>

              {/* Products Tab */}
              <TabsContent value="products" className="space-y-4">
                <p className="text-slate-400 text-sm mb-4">Configure which products are available for this tenant and customize their branding.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(formData.products).map(([key, product]) => (
                    <Card key={key} className={`bg-slate-800 border-slate-700 ${!product.enabled ? 'opacity-50' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                              style={{ backgroundColor: product.color + '30' }}
                            >
                              <LayoutGrid className="w-5 h-5" style={{ color: product.color }} />
                            </div>
                            <div>
                              <Label className="text-white">{product.name}</Label>
                              <p className="text-xs text-slate-500">{key}</p>
                            </div>
                          </div>
                          <Switch
                            checked={product.enabled}
                            onCheckedChange={(checked) => updateProduct(key, 'enabled', checked)}
                          />
                        </div>
                        
                        <div className="space-y-3">
                          <Input
                            value={product.name}
                            onChange={(e) => updateProduct(key, 'name', e.target.value)}
                            placeholder="Product Name"
                            className="bg-slate-900 border-slate-600 text-white text-sm"
                          />
                          <Input
                            value={product.description}
                            onChange={(e) => updateProduct(key, 'description', e.target.value)}
                            placeholder="Description"
                            className="bg-slate-900 border-slate-600 text-white text-sm"
                          />
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-slate-400">Color:</Label>
                            <input
                              type="color"
                              value={product.color}
                              onChange={(e) => updateProduct(key, 'color', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer"
                            />
                            <Input
                              value={product.color}
                              onChange={(e) => updateProduct(key, 'color', e.target.value)}
                              className="bg-slate-900 border-slate-600 text-white text-sm flex-1"
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* Features Tab */}
              <TabsContent value="features" className="space-y-4">
                <p className="text-slate-400 text-sm mb-4">Toggle platform features for this tenant.</p>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                    <div>
                      <Label className="text-white">Show "Powered By" Badge</Label>
                      <p className="text-xs text-slate-500 mt-1">Display Aurous branding in the footer</p>
                    </div>
                    <Switch
                      checked={formData.features.showPoweredBy}
                      onCheckedChange={(checked) => updateFeature('showPoweredBy', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                    <div>
                      <Label className="text-white">Enable Dark Mode</Label>
                      <p className="text-xs text-slate-500 mt-1">Allow users to toggle dark/light theme</p>
                    </div>
                    <Switch
                      checked={formData.features.enableDarkMode}
                      onCheckedChange={(checked) => updateFeature('enableDarkMode', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                    <div>
                      <Label className="text-white">Enable AI Chatbot</Label>
                      <p className="text-xs text-slate-500 mt-1">Show the AI assistant chatbot</p>
                    </div>
                    <Switch
                      checked={formData.features.enableChatbot}
                      onCheckedChange={(checked) => updateFeature('enableChatbot', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                    <div>
                      <Label className="text-white">Enable Analytics</Label>
                      <p className="text-xs text-slate-500 mt-1">Track user behavior with Google Analytics</p>
                    </div>
                    <Switch
                      checked={formData.features.enableAnalytics}
                      onCheckedChange={(checked) => updateFeature('enableAnalytics', checked)}
                    />
                  </div>
                  
                  {formData.features.enableAnalytics && (
                    <div className="p-4 bg-slate-800 rounded-lg">
                      <Label className="text-white">Google Analytics ID</Label>
                      <Input
                        value={formData.features.analyticsId}
                        onChange={(e) => updateFeature('analyticsId', e.target.value)}
                        placeholder="G-XXXXXXXXXX"
                        className="bg-slate-900 border-slate-600 text-white mt-2"
                      />
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
              <Button variant="outline" onClick={resetForm} className="border-slate-600 text-slate-300 hover:bg-slate-800">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleCreateTenant} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingTenant ? 'Update Tenant' : 'Create Tenant'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tenant List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tenants.map((tenant) => (
          <TenantCard
            key={tenant.subdomain}
            tenant={tenant}
            onEdit={startEditing}
            onDelete={handleDeleteTenant}
            onView={setPreviewTenant}
          />
        ))}
      </div>

      {tenants.length === 0 && !showForm && (
        <Card className="bg-slate-900 border-slate-700 border-dashed">
          <CardContent className="py-12 text-center">
            <Palette className="w-12 h-12 mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Tenants Configured</h3>
            <p className="text-slate-400 mb-4">Create your first white-label tenant to get started.</p>
            <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create First Tenant
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
